/**
 * Meta Conversions API (Server-Side Event Tracking)
 * Sends events to Facebook's Conversions API for improved tracking accuracy
 * 
 * IMPORTANT: For production use, you should set up a server-side proxy endpoint
 * to handle Conversions API calls, as direct client-side calls may be blocked
 * by CORS policies. The access token should never be exposed in client-side code.
 * 
 * For Shopify, consider using:
 * - Shopify Functions (serverless functions)
 * - App Proxy (custom endpoint)
 * - Third-party service
 */

// Configuration
const ConversionsAPI = {
  pixelId: '2847308042268459',
  accessToken: 'EAATtgetn4xYBQGEZCAq5gO9K8h7XTlyN6QBIGKv3MCb4X4PDoguREZCOwBERiSDZAiQocXPZBgWl0faH57SPdLQuUxmFqgjEo8zvkc8Kp7bg0ScwsLp4vt4sT3ZCMA2ZCqFXispQ5XYZBpdCYBtJCctjT8d7ppcZCjiZC8SbLMwHjc1F6bMR11QZAVKSSWbboEpQZDZD',
  apiVersion: 'v21.0',
  testEventCode: 'TEST57786', // Test event code for Facebook Events Manager
  serverProxyUrl: null, // Set this to your server-side proxy endpoint (e.g., '/apps/conversions-api')
  eventQueue: [], // Queue for events if direct calls fail
  
  /**
   * Hash user data using SHA-256
   * @param {string} value - Value to hash
   * @returns {Promise<string>} - Hashed value
   */
  async hashUserData(value) {
    if (!value) return null;
    
    try {
      // Convert string to ArrayBuffer
      const encoder = new TextEncoder();
      const data = encoder.encode(value.toLowerCase().trim());
      
      // Hash using Web Crypto API
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      
      // Convert ArrayBuffer to hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      return hashHex;
    } catch (error) {
      console.error('Error hashing user data:', error);
      return null;
    }
  },

  /**
   * Get user data from browser/cart
   * @returns {Promise<Object>} - User data object
   */
  async getUserData() {
    const userData = {
      client_user_agent: navigator.userAgent,
      client_ip_address: null, // Will be set server-side if available
      fbp: this.getCookie('_fbp'),
      fbc: this.getCookie('_fbc')
    };

    // Hash email if available (from customer data or form)
    const email = this.getEmailFromPage();
    if (email) {
      const hashedEmail = await this.hashUserData(email);
      if (hashedEmail) {
        userData.em = [hashedEmail];
      }
    }

    // Hash phone if available
    const phone = this.getPhoneFromPage();
    if (phone) {
      const hashedPhone = await this.hashUserData(phone.replace(/\D/g, ''));
      if (hashedPhone) {
        userData.ph = [hashedPhone];
      }
    }

    // Get first name, last name, city, state, zip from customer data
    const customerData = this.getCustomerData();
    if (customerData.firstName) {
      const hashedFirstName = await this.hashUserData(customerData.firstName);
      if (hashedFirstName) userData.fn = [hashedFirstName];
    }
    if (customerData.lastName) {
      const hashedLastName = await this.hashUserData(customerData.lastName);
      if (hashedLastName) userData.ln = [hashedLastName];
    }
    if (customerData.city) {
      const hashedCity = await this.hashUserData(customerData.city);
      if (hashedCity) userData.ct = [hashedCity];
    }
    if (customerData.state) {
      const hashedState = await this.hashUserData(customerData.state);
      if (hashedState) userData.st = [hashedState];
    }
    if (customerData.zip) {
      const hashedZip = await this.hashUserData(customerData.zip);
      if (hashedZip) userData.zp = [hashedZip];
    }

    return userData;
  },

  /**
   * Get cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  },

  /**
   * Extract email from page (forms, customer data, etc.)
   * @returns {string|null} - Email address
   */
  getEmailFromPage() {
    // Check for customer email in Shopify
    if (typeof window.Shopify?.customer?.email !== 'undefined') {
      return window.Shopify.customer.email;
    }
    
    // Check for email in forms
    const emailInputs = document.querySelectorAll('input[type="email"]');
    for (const input of emailInputs) {
      if (input.value) return input.value;
    }
    
    return null;
  },

  /**
   * Extract phone from page
   * @returns {string|null} - Phone number
   */
  getPhoneFromPage() {
    // Check for phone in forms
    const phoneInputs = document.querySelectorAll('input[type="tel"], input[name*="phone"], input[id*="phone"]');
    for (const input of phoneInputs) {
      if (input.value) return input.value;
    }
    
    return null;
  },

  /**
   * Get customer data from Shopify
   * @returns {Object} - Customer data
   */
  getCustomerData() {
    const data = {
      firstName: null,
      lastName: null,
      city: null,
      state: null,
      zip: null
    };

    if (typeof window.Shopify?.customer !== 'undefined') {
      data.firstName = window.Shopify.customer.first_name;
      data.lastName = window.Shopify.customer.last_name;
      data.city = window.Shopify.customer.default_address?.city;
      data.state = window.Shopify.customer.default_address?.province;
      data.zip = window.Shopify.customer.default_address?.zip;
    }

    return data;
  },

  /**
   * Generate event ID for deduplication
   * @param {string} eventName - Event name
   * @returns {string} - Event ID
   */
  generateEventId(eventName) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `${eventName}_${timestamp}_${random}`;
  },

  /**
   * Send event to Conversions API
   * @param {Object} eventData - Event data
   * @returns {Promise<Object>} - API response
   */
  async sendEvent(eventData) {
    try {
      // Get user data
      const userData = await this.getUserData();

      // Build the event payload
      const payload = {
        data: [
          {
            event_name: eventData.event_name,
            event_time: Math.floor(Date.now() / 1000),
            action_source: 'website',
            event_id: eventData.event_id || this.generateEventId(eventData.event_name),
            event_source_url: window.location.href,
            user_data: userData,
            custom_data: eventData.custom_data || {}
          }
        ]
      };

      // Add test event code if set
      if (this.testEventCode) {
        payload.test_event_code = this.testEventCode;
      }

      // If server proxy URL is set, use it (recommended for production)
      if (this.serverProxyUrl) {
        try {
          const response = await fetch(this.serverProxyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Conversions API proxy error:', errorText);
            return { success: false, error: errorText };
          }

          const result = await response.json();
          return { success: true, data: result };
        } catch (error) {
          console.error('Error sending event via proxy:', error);
          // Fall through to direct API call
        }
      }

      // Direct API call (will fail due to CORS - server-side proxy required)
      // Log the payload for debugging
      console.log('Conversions API Payload:', JSON.stringify(payload, null, 2));
      
      const url = `https://graph.facebook.com/${this.apiVersion}/${this.pixelId}/events?access_token=${this.accessToken}`;
      
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Facebook API Error:', errorData);
          return { success: false, error: errorData };
        }

        const result = await response.json();
        console.log('Facebook API Success:', result);
        return { success: true, data: result };
      } catch (error) {
        // CORS error is expected - events need to be sent via server-side proxy
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          console.warn('CORS blocked: Direct browser calls to Facebook API are not allowed.');
          console.warn('Payload structure is correct. Set up server-side proxy to send events.');
          console.warn('Payload that would be sent:', JSON.stringify(payload, null, 2));
          this.eventQueue.push({ payload, timestamp: Date.now() });
          return { 
            success: false, 
            error: 'CORS blocked - server-side proxy required',
            payload: payload // Return payload for verification
          };
        }
        console.error('Error sending event:', error);
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error('Error sending event to Conversions API:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Track AddToCart event
   * @param {Object} data - Event data
   */
  async trackAddToCart(data = {}) {
    // Ensure content_ids is an array of strings
    let contentIds = [];
    if (Array.isArray(data.content_ids)) {
      contentIds = data.content_ids.map(id => String(id));
    } else if (data.content_ids) {
      contentIds = [String(data.content_ids)];
    }
    
    return await this.sendEvent({
      event_name: 'AddToCart',
      custom_data: {
        content_name: data.content_name || '',
        content_ids: contentIds,
        content_type: data.content_type || 'product',
        value: parseFloat(data.value) || 0.00,
        currency: data.currency || 'USD'
      }
    });
  },

  /**
   * Track ViewContent event
   * @param {Object} data - Event data
   */
  async trackViewContent(data = {}) {
    // Ensure content_ids is an array of strings
    let contentIds = [];
    if (Array.isArray(data.content_ids)) {
      contentIds = data.content_ids.map(id => String(id));
    } else if (data.content_ids) {
      contentIds = [String(data.content_ids)];
    }
    
    return await this.sendEvent({
      event_name: 'ViewContent',
      custom_data: {
        content_name: data.content_name || '',
        content_ids: contentIds,
        content_type: data.content_type || 'product',
        value: parseFloat(data.value) || 0.00,
        currency: data.currency || 'USD'
      }
    });
  },

  /**
   * Track Search event
   * @param {string} searchString - Search query
   */
  async trackSearch(searchString) {
    return await this.sendEvent({
      event_name: 'Search',
      custom_data: {
        search_string: searchString || ''
      }
    });
  },

  /**
   * Track InitiateCheckout event
   * @param {Object} data - Event data
   */
  async trackInitiateCheckout(data = {}) {
    return await this.sendEvent({
      event_name: 'InitiateCheckout',
      custom_data: {
        value: data.value || 0.00,
        currency: data.currency || 'USD',
        num_items: data.num_items || 0
      }
    });
  },

  /**
   * Track Purchase event
   * @param {Object} data - Event data
   */
  async trackPurchase(data = {}) {
    // Ensure content_ids is an array of strings
    let contentIds = [];
    if (Array.isArray(data.content_ids)) {
      contentIds = data.content_ids.map(id => String(id));
    } else if (data.content_ids) {
      contentIds = [String(data.content_ids)];
    }
    
    return await this.sendEvent({
      event_name: 'Purchase',
      custom_data: {
        value: parseFloat(data.value) || 0.00,
        currency: data.currency || 'USD',
        content_ids: contentIds,
        content_type: data.content_type || 'product',
        num_items: parseInt(data.num_items) || 0
      }
    });
  },

  /**
   * Track CompleteRegistration event
   */
  async trackCompleteRegistration() {
    return await this.sendEvent({
      event_name: 'CompleteRegistration'
    });
  },

  /**
   * Track Contact event
   */
  async trackContact() {
    return await this.sendEvent({
      event_name: 'Contact'
    });
  },

  /**
   * Track Subscribe event
   * @param {Object} data - Event data
   */
  async trackSubscribe(data = {}) {
    return await this.sendEvent({
      event_name: 'Subscribe',
      custom_data: {
        value: data.value || '0.00',
        currency: data.currency || 'USD',
        predicted_ltv: data.predicted_ltv || '0.00'
      }
    });
  },

  /**
   * Track Lead event
   */
  async trackLead() {
    return await this.sendEvent({
      event_name: 'Lead'
    });
  },

  /**
   * Track AddPaymentInfo event
   */
  async trackAddPaymentInfo() {
    return await this.sendEvent({
      event_name: 'AddPaymentInfo'
    });
  },

  /**
   * Track AddToWishlist event
   * @param {Object} data - Event data
   */
  async trackAddToWishlist(data = {}) {
    return await this.sendEvent({
      event_name: 'AddToWishlist',
      custom_data: {
        content_name: data.content_name || '',
        content_ids: Array.isArray(data.content_ids) ? data.content_ids : [data.content_ids],
        content_type: data.content_type || 'product',
        value: data.value || 0.00,
        currency: data.currency || 'USD'
      }
    });
  },

  /**
   * Track CustomizeProduct event
   */
  async trackCustomizeProduct() {
    return await this.sendEvent({
      event_name: 'CustomizeProduct'
    });
  },

  /**
   * Track Donate event
   * @param {Object} data - Event data
   */
  async trackDonate(data = {}) {
    return await this.sendEvent({
      event_name: 'Donate',
      custom_data: {
        value: data.value || 0.00,
        currency: data.currency || 'USD'
      }
    });
  },

  /**
   * Track FindLocation event
   */
  async trackFindLocation() {
    return await this.sendEvent({
      event_name: 'FindLocation'
    });
  },

  /**
   * Track Schedule event
   */
  async trackSchedule() {
    return await this.sendEvent({
      event_name: 'Schedule'
    });
  },

  /**
   * Track StartTrial event
   * @param {Object} data - Event data
   */
  async trackStartTrial(data = {}) {
    return await this.sendEvent({
      event_name: 'StartTrial',
      custom_data: {
        value: data.value || '0.00',
        currency: data.currency || 'USD',
        predicted_ltv: data.predicted_ltv || '0.00'
      }
    });
  },

  /**
   * Track SubmitApplication event
   */
  async trackSubmitApplication() {
    return await this.sendEvent({
      event_name: 'SubmitApplication'
    });
  }
};

// Make it globally available
window.ConversionsAPI = ConversionsAPI;

// Test function to verify payload structure
window.ConversionsAPI.testPayload = async function() {
  console.log('🧪 Testing Conversions API Payload Structure...');
  
  const testData = {
    content_name: 'Test Product',
    content_ids: ['12345'],
    value: 29.99,
    currency: 'USD'
  };
  
  // Get user data to see what will be sent
  const userData = await this.getUserData();
  console.log('📊 User Data:', userData);
  
  // Build test payload
  const testPayload = {
    data: [{
      event_name: 'ViewContent',
      event_time: Math.floor(Date.now() / 1000),
      action_source: 'website',
      event_id: this.generateEventId('ViewContent'),
      event_source_url: window.location.href,
      user_data: userData,
      custom_data: {
        content_name: testData.content_name,
        content_ids: [String(testData.content_ids[0])],
        content_type: 'product',
        value: parseFloat(testData.value),
        currency: testData.currency
      }
    }]
  };
  
  if (this.testEventCode) {
    testPayload.test_event_code = this.testEventCode;
  }
  
  console.log('✅ Payload Structure:', JSON.stringify(testPayload, null, 2));
  console.log('📝 Test Event Code:', this.testEventCode || 'Not set');
  console.log('🔗 API URL:', `https://graph.facebook.com/${this.apiVersion}/${this.pixelId}/events`);
  
  // Validate payload structure
  const errors = [];
  if (!testPayload.data || !Array.isArray(testPayload.data)) {
    errors.push('❌ data must be an array');
  }
  if (!testPayload.data[0].event_name) {
    errors.push('❌ event_name is required');
  }
  if (!testPayload.data[0].event_time) {
    errors.push('❌ event_time is required');
  }
  if (!testPayload.data[0].action_source) {
    errors.push('❌ action_source is required');
  }
  if (testPayload.data[0].custom_data.content_ids && !Array.isArray(testPayload.data[0].custom_data.content_ids)) {
    errors.push('❌ content_ids must be an array');
  }
  
  if (errors.length > 0) {
    console.error('❌ Payload Validation Errors:', errors);
  } else {
    console.log('✅ Payload structure is valid!');
  }
  
  return testPayload;
};

